import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { User } from "../../types/Types";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { isPaidPortalMember } from "../../utils/membershipTier";
import {
  AccountBodyText,
  AccountContactRow,
  AccountProfileCard,
  AccountTagField,
  AccountTagGrid,
} from "./AccountProfileLayout";

interface YouthProfileProps {
  userData: User;
}

function useYouthProfileCopy(userData: User) {
  const { t, i18n } = useTranslation();
  const np = () => t("account.notProvided");
  const dynamicFields = {
    aboutYou: userData.aboutYou,
    email: userData.email,
    phoneNumber: userData.phoneNumber,
    languagePreference: userData.languagePreference,
    gender: userData.gender,
    educationLevel: userData.educationLevel,
    studyInterest: userData.studyInterest,
    mentorshipObjectives: userData.mentorshipObjectives,
    mentorshipExpertise: userData.mentorshipExpertise,
    mentorshipFormat: userData.mentorshipFormat,
    platformInterest: userData.platformInterest,
    peerNetworking: userData.peerNetworking,
    membershipName: userData.membershipName,
    referralCode: userData.referralCode,
    referralCodeUsed: userData.referralCodeUsed,
  };
  const { data: translated } = useTranslateDynamicFields(
    dynamicFields,
    i18n.resolvedLanguage || i18n.language,
  );
  const tx = (key: keyof typeof dynamicFields, fallback?: string | null) =>
    translated?.[key] || fallback || np();

  const formattedDateOfBirth = useMemo(
    () =>
      userData.dateOfBirth
        ? new Date(userData.dateOfBirth).toLocaleDateString(
            i18n.resolvedLanguage || i18n.language || "en",
          )
        : "",
    [i18n.language, i18n.resolvedLanguage, userData.dateOfBirth],
  );

  return {
    t,
    np,
    tx,
    formattedDateOfBirth,
    showMembershipFields: isPaidPortalMember(userData),
  };
}

const YouthProfile = memo(({ userData }: YouthProfileProps) => {
  const { t, tx } = useYouthProfileCopy(userData);
  const aboutText = tx("aboutYou", userData.aboutYou);

  return (
    <div dir="ltr">
      <AccountProfileCard title={t("account.section.about")}>
        <AccountBodyText
          textForMeasure={aboutText}
          label={t("account.section.about")}
        >
          {aboutText}
        </AccountBodyText>
      </AccountProfileCard>

      <AccountProfileCard title={t("account.section.education")}>
        <AccountTagGrid>
          <AccountTagField
            label={t("account.field.educationLevel")}
            value={tx("educationLevel", userData.educationLevel)}
            textValue={tx("educationLevel", userData.educationLevel)}
          />
          <AccountTagField
            label={t("account.field.fieldOfStudyInterest")}
            value={tx("studyInterest", userData.studyInterest)}
            textValue={tx("studyInterest", userData.studyInterest)}
          />
        </AccountTagGrid>
      </AccountProfileCard>

      <AccountProfileCard title={t("account.section.interests")}>
        <AccountTagGrid>
          <AccountTagField
            label={t("account.field.mentorshipObjectives")}
            value={tx("mentorshipObjectives", userData.mentorshipObjectives)}
            textValue={tx("mentorshipObjectives", userData.mentorshipObjectives)}
          />
          <AccountTagField
            label={t("account.field.preferredMentorshipExpertise")}
            value={tx("mentorshipExpertise", userData.mentorshipExpertise)}
            textValue={tx("mentorshipExpertise", userData.mentorshipExpertise)}
          />
          <AccountTagField
            label={t("account.field.preferredMentorshipFormat")}
            value={tx("mentorshipFormat", userData.mentorshipFormat)}
            textValue={tx("mentorshipFormat", userData.mentorshipFormat)}
          />
          <AccountTagField
            label={t("account.field.areasOfInterestPlatform")}
            value={tx("platformInterest", userData.platformInterest)}
            textValue={tx("platformInterest", userData.platformInterest)}
          />
          <AccountTagField
            label={t("account.field.interestedPeerNetworking")}
            value={tx("peerNetworking", userData.peerNetworking)}
            textValue={tx("peerNetworking", userData.peerNetworking)}
          />
        </AccountTagGrid>
      </AccountProfileCard>
    </div>
  );
});

YouthProfile.displayName = "YouthProfile";

export function YouthMembershipTags({ userData }: YouthProfileProps) {
  const { t, tx, showMembershipFields } = useYouthProfileCopy(userData);

  return (
    <AccountTagGrid>
      {showMembershipFields ? (
        <>
          <AccountTagField
            label={t("account.field.membershipName")}
            value={tx("membershipName", userData.membershipName)}
            textValue={tx("membershipName", userData.membershipName)}
          />
          <AccountTagField
            label={t("account.field.membershipType")}
            value={
              userData.isAnnualMembership
                ? t("account.billingCycle.annual")
                : t("account.billingCycle.monthly")
            }
            textValue={
              userData.isAnnualMembership
                ? t("account.billingCycle.annual")
                : t("account.billingCycle.monthly")
            }
          />
        </>
      ) : null}
      <AccountTagField
        label={t("account.field.referralCode")}
        value={tx("referralCode", userData.referralCode)}
        textValue={tx("referralCode", userData.referralCode)}
      />
      {userData.referralCodeUsed ? (
        <AccountTagField
          label={t("account.field.referralCodeUsed")}
          value={tx("referralCodeUsed", userData.referralCodeUsed)}
          textValue={tx("referralCodeUsed", userData.referralCodeUsed)}
        />
      ) : null}
    </AccountTagGrid>
  );
}

export function YouthContactDetails({ userData }: YouthProfileProps) {
  const { t, np, tx, formattedDateOfBirth } = useYouthProfileCopy(userData);

  const row = (label: string, value: string) => (
    <AccountContactRow label={label} value={value} title={value} />
  );

  return (
    <>
      {row(t("account.field.email"), tx("email", userData.email))}
      {row(t("account.field.contactNumber"), tx("phoneNumber", userData.phoneNumber))}
      {row(
        t("account.field.languagePreference"),
        tx("languagePreference", userData.languagePreference),
      )}
      {row(t("account.field.gender"), tx("gender", userData.gender))}
      {row(t("account.field.dateOfBirth"), formattedDateOfBirth || np())}
    </>
  );
}

export default YouthProfile;
